import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import DatasetCatalog from './components/DatasetCatalog';
import ResearchSandbox from './components/ResearchSandbox';
import VisualizationTools from './components/VisualizationTools';
import AccessManagement from './components/AccessManagement';
import ExportTools from './components/ExportTools';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const ResearchDataPortal = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [queryResults, setQueryResults] = useState(null);
  const [activePanel, setActivePanel] = useState('catalog');

  const userProfile = {
    name: 'Dr. Elena Rossi',
    institution: 'University of Bologna',
    role: 'Senior Research Fellow',
    accessLevel: 'academic',
    verificationStatus: 'verified',
    activeProjects: 3
  };

  const panelOptions = [
    { id: 'catalog', label: 'Dataset Catalog', icon: 'Database' },
    { id: 'sandbox', label: 'Research Sandbox', icon: 'Code' },
    { id: 'visualization', label: 'Visualization', icon: 'BarChart3' },
    { id: 'access', label: 'Access Management', icon: 'Key' },
    { id: 'export', label: 'Export Tools', icon: 'Download' }
  ];

  const handleDatasetSelect = (dataset) => {
    setSelectedDataset(dataset);
    if (activePanel === 'catalog') {
      setActivePanel('sandbox');
    }
  };

  const handleQueryExecute = (results) => {
    setQueryResults(results);
    setActivePanel('visualization');
  };

  const handleAccessRequest = (request) => {
    console.log('Access request submitted:', request);
    // Handle access request logic
  };

  const renderActivePanel = () => {
    switch (activePanel) {
      case 'catalog':
        return (
          <DatasetCatalog
            onDatasetSelect={handleDatasetSelect}
            selectedDataset={selectedDataset}
          />
        );
      case 'sandbox':
        return (
          <ResearchSandbox
            selectedDataset={selectedDataset}
            onQueryExecute={handleQueryExecute}
          />
        );
      case 'visualization':
        return (
          <VisualizationTools
            queryResults={queryResults}
            selectedDataset={selectedDataset}
          />
        );
      case 'access':
        return (
          <AccessManagement
            userProfile={userProfile}
            onAccessRequest={handleAccessRequest}
          />
        );
      case 'export':
        return (
          <ExportTools
            selectedDataset={selectedDataset}
            queryResults={queryResults}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main className={`
        transition-all duration-300 ease-in-out pt-16
        ${sidebarCollapsed ? 'ml-16' : 'ml-64'}
      `}>
        <div className="p-6">
          {/* Page Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Research Data Portal</h1>
                <p className="text-muted-foreground mt-2">
                  DSA-compliant access to pseudonymized datasets and analytical tools for academic research
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2 px-3 py-2 bg-success/10 rounded-lg">
                  <Icon name="Shield" size={16} className="text-success" />
                  <span className="text-sm font-medium text-success">Verified Researcher</span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-foreground">{userProfile?.name}</div>
                  <div className="text-xs text-muted-foreground">{userProfile?.institution}</div>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-card p-4 rounded-lg border border-border shadow-soft">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Icon name="Database" size={20} className="text-primary" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-foreground">127</div>
                    <div className="text-sm text-muted-foreground">Available Datasets</div>
                  </div>
                </div>
              </div>
              <div className="bg-card p-4 rounded-lg border border-border shadow-soft">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-success/10 rounded-lg">
                    <Icon name="Users" size={20} className="text-success" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-foreground">2,847</div>
                    <div className="text-sm text-muted-foreground">Active Researchers</div>
                  </div>
                </div>
              </div>
              <div className="bg-card p-4 rounded-lg border border-border shadow-soft">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-warning/10 rounded-lg">
                    <Icon name="Download" size={20} className="text-warning" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-foreground">45.2TB</div>
                    <div className="text-sm text-muted-foreground">Data Processed</div>
                  </div>
                </div>
              </div>
              <div className="bg-card p-4 rounded-lg border border-border shadow-soft">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-accent/10 rounded-lg">
                    <Icon name="FileText" size={20} className="text-accent" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-foreground">1,234</div>
                    <div className="text-sm text-muted-foreground">Publications</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Panel Navigation */}
            <div className="flex flex-wrap gap-2 mb-6">
              {panelOptions?.map((panel) => (
                <button
                  key={panel?.id}
                  onClick={() => setActivePanel(panel?.id)}
                  className={`
                    flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-smooth
                    ${activePanel === panel?.id
                      ? 'bg-primary text-primary-foreground shadow-soft'
                      : 'bg-card text-muted-foreground hover:text-foreground hover:bg-muted border border-border'
                    }
                  `}
                >
                  <Icon name={panel?.icon} size={16} />
                  <span>{panel?.label}</span>
                </button>
              ))}
            </div>

            {/* Context Information */}
            {selectedDataset && (
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Icon name="Database" size={20} className="text-primary" />
                    <div>
                      <div className="text-sm font-medium text-foreground">
                        Active Dataset: {selectedDataset?.title}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {selectedDataset?.size} • {new Intl.NumberFormat('en-EU')?.format(selectedDataset?.records)} records • 
                        Updated {new Date(selectedDataset.lastUpdated)?.toLocaleDateString('en-GB')}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="X"
                    onClick={() => setSelectedDataset(null)}
                  >
                    Clear Selection
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Main Content Area */}
          <div className="space-y-6">
            {renderActivePanel()}
          </div>

          {/* Footer Information */}
          <div className="mt-12 pt-8 border-t border-border">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-muted-foreground">
              <div>
                <h3 className="font-medium text-foreground mb-2">Data Protection</h3>
                <ul className="space-y-1">
                  <li>• GDPR compliant data processing</li>
                  <li>• Automated PII removal</li>
                  <li>• Configurable anonymization levels</li>
                  <li>• Audit trail for all access</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-foreground mb-2">Research Support</h3>
                <ul className="space-y-1">
                  <li>• Citation-ready exports</li>
                  <li>• Academic licensing compliance</li>
                  <li>• Methodology documentation</li>
                  <li>• Peer collaboration tools</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-foreground mb-2">Technical Standards</h3>
                <ul className="space-y-1">
                  <li>• DSA-aligned access controls</li>
                  <li>• Multi-format data exports</li>
                  <li>• Real-time data processing</li>
                  <li>• API integration support</li>
                </ul>
              </div>
            </div>
            
            <div className="mt-6 pt-4 border-t border-border text-center">
              <p className="text-xs text-muted-foreground">
                © {new Date()?.getFullYear()} TruthGuard EU Research Data Portal. All rights reserved. 
                Data access subject to institutional agreements and ethical approval.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ResearchDataPortal;